import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Users, 
  MessageSquare, 
  Calendar, 
  Clock, 
  DollarSign,
  Star,
  AlertCircle,
  CheckCircle,
  XCircle,
  Video,
  Phone,
  User
} from 'lucide-react';
import { useApp } from '../context/useApp';
import { dashboardService } from '../services/dashboardService';

const LawyerDashboardMetrics = () => {
  const { user, language } = useApp();
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalClients: 0,
    totalEarnings: 0,
    pendingCases: 0,
    messages: 0,
    availableBalance: 0,
    monthlyEarnings: 0,
    averageRating: 0,
    totalReviews: 0,
    consultationTypes: {
      videoCall: { count: 0, earnings: 0 },
      phoneCall: { count: 0, earnings: 0 },
      inPerson: { count: 0, earnings: 0 }
    },
    lastActivity: null
  });

  const translations = {
    english: {
      totalClients: 'Total Clients',
      totalEarnings: 'Total Earnings',
      pendingCases: 'Pending Cases',
      messages: 'Messages',
      availableBalance: 'Available Balance',
      monthlyEarnings: 'Monthly Earnings',
      averageRating: 'Average Rating',
      totalReviews: 'Total Reviews',
      videoCall: 'Video Call',
      phoneCall: 'Phone Call',
      inPerson: 'In Person',
      sessions: 'sessions',
      lastActivity: 'Last Activity',
      minutes: 'min',
      hours: 'hrs',
      days: 'days',
      noActivity: 'No recent activity'
    },
    hindi: {
      totalClients: 'कुल ग्राहक',
      totalEarnings: 'कुल कमाई',
      pendingCases: 'लंबित मामले',
      messages: 'संदेश',
      availableBalance: 'उपलब्ध बैलेंस',
      monthlyEarnings: 'मासिक कमाई',
      averageRating: 'औसत रेटिंग',
      totalReviews: 'कुल समीक्षाएं',
      videoCall: 'वीडियो कॉल',
      phoneCall: 'फोन कॉल',
      inPerson: 'व्यक्तिगत',
      sessions: 'सत्र',
      lastActivity: 'अंतिम गतिविधि',
      minutes: 'मिनट',
      hours: 'घंटे',
      days: 'दिन',
      noActivity: 'कोई हाल की गतिविधि नहीं'
    }
  };

  const t = translations[language] || translations.english;

  useEffect(() => {
    if (user?.uid) {
      loadLawyerMetrics();
      setupRealtimeUpdates();
    }
  }, [user?.uid]);

  const loadLawyerMetrics = async () => {
    try {
      setIsLoading(true);
      const result = await dashboardService.calculateLawyerDashboardMetrics(user.uid);
      if (result.success) {
        setMetrics(result.data);
      }
    } catch (error) {
      console.error('Error loading lawyer metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setupRealtimeUpdates = () => {
    if (!user?.uid) return;

    const unsubscribe = dashboardService.subscribeToLawyerDashboardUpdates(user.uid, (update) => {
      if (update.type === 'lawyer_dashboard_update' && update.metrics) {
        setMetrics(update.metrics);
      }
    });

    return unsubscribe;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return t.noActivity;
    
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${t.minutes} ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} ${t.hours} ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} ${t.days} ago`;
    }
  };

  const metricCards = [
    {
      id: 'totalClients',
      title: t.totalClients,
      value: metrics.totalClients,
      icon: Users,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      id: 'totalEarnings',
      title: t.totalEarnings,
      value: formatCurrency(metrics.totalEarnings),
      icon: DollarSign,
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600'
    },
    {
      id: 'pendingCases',
      title: t.pendingCases,
      value: metrics.pendingCases,
      icon: AlertCircle,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600'
    },
    {
      id: 'messages',
      title: t.messages,
      value: metrics.messages,
      icon: MessageSquare,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    }
  ];

  const consultationCards = [
    {
      id: 'videoCall',
      title: t.videoCall,
      price: '₹150',
      sessions: metrics.consultationTypes.videoCall.count,
      earnings: metrics.consultationTypes.videoCall.earnings,
      icon: Video,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      id: 'phoneCall',
      title: t.phoneCall,
      price: '₹120',
      sessions: metrics.consultationTypes.phoneCall.count,
      earnings: metrics.consultationTypes.phoneCall.earnings,
      icon: Phone,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      id: 'inPerson',
      title: t.inPerson,
      price: '₹300',
      sessions: metrics.consultationTypes.inPerson.count,
      earnings: metrics.consultationTypes.inPerson.earnings,
      icon: User,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    }
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Metrics Grid Loading */}
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 animate-pulse">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                <div className="w-12 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
              <div className="w-16 h-6 bg-gray-200 dark:bg-gray-700 rounded mb-1"></div>
              <div className="w-20 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          ))}
        </div>

        {/* Consultation Types Loading */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Consultation Types</h3>
          <div className="grid grid-cols-1 gap-4">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    <div className="space-y-2">
                      <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      <div className="w-32 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  </div>
                  <div className="text-right space-y-2">
                    <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-20 h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 gap-4">
        {metricCards.map((card, index) => (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <card.icon className={`w-5 h-5 ${card.textColor}`} />
              </div>
              <div className={`w-2 h-2 rounded-full ${card.color}`}></div>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {card.value}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {card.title}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Consultation Types */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="space-y-4"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Consultation Types</h3>
        <div className="grid grid-cols-1 gap-4">
          {consultationCards.map((card, index) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${card.bgColor}`}>
                    <card.icon className={`w-6 h-6 ${card.textColor}`} />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{card.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{card.price}/session</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{card.sessions} {t.sessions}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{formatCurrency(card.earnings)}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Performance Stats</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">{t.averageRating}</span>
              </div>
              <span className="font-medium text-gray-900 dark:text-white">
                {metrics.averageRating}/5.0
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">{t.totalReviews}</span>
              </div>
              <span className="font-medium text-gray-900 dark:text-white">
                {metrics.totalReviews}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">{t.monthlyEarnings}</span>
              </div>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatCurrency(metrics.monthlyEarnings)}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Recent Activity</h3>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t.lastActivity}</span>
          </div>
          <div className="text-lg font-medium text-gray-900 dark:text-white mt-1">
            {formatTimeAgo(metrics.lastActivity)}
          </div>
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Last updated: {new Date().toLocaleTimeString()}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LawyerDashboardMetrics;
